import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import Link from "next/link";
import {
  Button,
  Colors,
  Icon,
  IconName,
  ServiceIcon,
  Text,
} from "@zapier/design-system";
import {
  editingNodeAtom,
  NodeType,
  DefaultNodeData,
  ZapMetadata,
  floatingToolbarActiveButtonAtom,
  serviceAtomFamily,
  AssetType,
  ZapNodeData,
} from "@zapier/canvas-state";
import { useAtomValue, useSetAtom } from "jotai";
import { NodeProps, Node } from "@xyflow/react";

import { StepTitle } from "../../../../components/StepTitle";
import capitalizeFirstLetter from "../../../../utils/capitalizeFirstLetter";
import { determineAssetType } from "../../../../utils/determineAssetType";
import { usePreviewContext } from "../../../../context/PreviewProvider";
import { lightenColor } from "../../../../utils/lightenColor";
import { UnstyledButton } from "../../../../components/UnstyledButton/UnstyledButton";

import styles from "./AssetNodeSkeleton.module.css";
import { AssetPreview } from "./AssetPreview";
import { getContrastingTextColor } from "./utils";

type Props = {
  className: string;
  placeholder: string;
  zap?: ZapMetadata;
  isZapExpanded?: boolean;
  isZapNotFound?: boolean;
  isViewOnlyMode?: boolean;
  iconName?: IconName;
  onUpdateNode?: (
    data: Partial<ZapNodeData>,
    options?: { shouldSave: boolean },
  ) => void;
};

export function AssetNodeSkeleton(
  props: NodeProps<Node<DefaultNodeData>> & Props,
) {
  const { isPreview } = usePreviewContext();
  const {
    id,
    data,
    type,
    selected,
    className,
    isViewOnlyMode = false,
    iconName,
    zap,
    onUpdateNode,
  } = props;
  const [hoveringOverEditButton, setHoveringOverEditButton] = useState(false);
  const { link, color, label } = data;
  const appId = data.services?.[0]?.currentImplementation.id ?? "";
  const setEditingNode = useSetAtom(editingNodeAtom);

  const setActiveButton = useSetAtom(floatingToolbarActiveButtonAtom);
  const app = useAtomValue(serviceAtomFamily(appId));
  const assetType = determineAssetType(data.services, type as NodeType);
  const asset = getAssetId(data, assetType);

  const openFloatingLinkInput = () => {
    setActiveButton("link");
  };

  const openEditModal = () => {
    setEditingNode(id);
  };

  const zapTitle = zap?.title ?? "";
  const hasSetZapTitleAsStepTitle = useRef(false);

  useEffect(() => {
    // If there was no step title, set the Zap title as the step title
    if (
      !hasSetZapTitleAsStepTitle.current &&
      !label &&
      zapTitle &&
      onUpdateNode
    ) {
      onUpdateNode({ label: zapTitle }, { shouldSave: true });
      /* Mark hasSetZapTitleAsStepTitle as true to prevent this logic from running again.
       * We only want this to happen on page load, not when user empties the input manually */
      hasSetZapTitleAsStepTitle.current = true;
    }
  }, [label, zapTitle, onUpdateNode]);

  const onMouseOverEditButton = (hovering: boolean) => {
    setHoveringOverEditButton(hovering);
  };

  return (
    <div
      className={classNames(
        styles.container,
        styles[assetType || "default"],
        className,
      )}
      style={{
        backgroundColor: color,
      }}
    >
      <div className={styles.bodyContainer}>
        <div className={styles.whiteBackground}>
          <div
            className={styles.body}
            style={{
              backgroundColor: lightenColor(
                color || Colors.StrokeWeaker,
                0.2,
                1.2,
              ),
            }}
          >
            <div
              className={styles.previewImage}
              style={
                !assetType ? { backgroundColor: `#${app?.primary_color}` } : {}
              }
              onFocus={() => onMouseOverEditButton(true)}
              onMouseOver={() => onMouseOverEditButton(true)}
              onBlur={() => onMouseOverEditButton(false)}
              onMouseLeave={() => onMouseOverEditButton(false)}
            >
              {assetType && <AssetPreview type={assetType} />}
              {app?.images?.url_128x128 && !assetType && (
                <ServiceIcon
                  serviceName={app?.name}
                  size="large"
                  src={app?.images?.url_128x128}
                />
              )}
              {!isViewOnlyMode && !isPreview && !!assetType && (
                <span
                  className={classNames(styles.editButton, {
                    [styles.hovered]: hoveringOverEditButton,
                  })}
                >
                  <Button
                    size="medium"
                    color="primary"
                    iconBefore="actionEditAi"
                    onClick={openEditModal}
                  >
                    {hoveringOverEditButton ? "Edit asset" : null}
                  </Button>
                </span>
              )}
            </div>
            <StepTitle
              nodeId={id}
              label={data.label}
              isSelected={Boolean(selected)}
              placeholder={props.placeholder}
              canNodeGetAiSuggestion={false}
              rows={2}
              isViewOnlyMode={isViewOnlyMode || isPreview}
            />
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        {iconName && (
          <div className={styles.iconContainer}>
            <Icon name={iconName} color="BrandOrange" size={16} />
          </div>
        )}
        {app?.images?.url_16x16 && !assetType && (
          <ServiceIcon
            serviceName={app?.name}
            size="small"
            src={app?.images?.url_16x16}
          />
        )}
        {!link && (
          <Text type="MinimalPrint1" color={getContrastingTextColor(color!)}>
            {capitalizeFirstLetter((assetType || app?.name) ?? "")}
          </Text>
        )}
        {link && (
          <>
            <UnstyledButton onClick={openFloatingLinkInput}>
              <Text
                color={getContrastingTextColor(color!)}
                type="MinimalPrint1"
              >
                {link}
              </Text>
            </UnstyledButton>
            <Link href={link} target="_blank" className={styles.offsiteLink}>
              <Icon name="arrowOffsite" size={15} />
            </Link>
          </>
        )}
        {assetType && !asset && (
          <UnstyledButton onClick={openFloatingLinkInput}>
            <Text
              type="MinimalPrint1"
              color={getContrastingTextColor(color!)}
              className={styles.underline}
            >
              Link existing {capitalizeFirstLetter(assetType)}
            </Text>
          </UnstyledButton>
        )}
      </div>
    </div>
  );
}

function getAssetId(data: any, assetType: AssetType) {
  if (assetType === "zap") {
    return data.zapId;
  }
  if (assetType === "table") {
    return data.tableId;
  }
  if (assetType === "interface") {
    return data.pageId;
  }
  if (assetType === "chatbot") {
    return data.chatbotId;
  }
  return null;
}
